import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { userApi } from '../api/client'

interface Props {
  open: boolean
}

export default function EmailPromptModal({ open }: Props) {
  const { t } = useTranslation()
  const { updateUser } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmed = email.trim()
    if (!trimmed) { setError(t('register.email.required')); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(t('register.email.invalid'))
      return
    }

    setSubmitting(true)
    try {
      const profile = await userApi.updateEmail(trimmed)
      updateUser({ email: profile.email })
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? t('register.email.saveFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* 반투명 오버레이 — 클릭해도 닫히지 않음 */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* 상단 강조 바 */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500" />

        <div className="px-8 pt-7 pb-8">
          {/* 아이콘 */}
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <polyline points="2,4 12,13 22,4" />
              </svg>
            </div>
          </div>

          <h2 className="text-center text-lg font-bold text-slate-900 mb-2">{t('register.email.title')}</h2>
          <p className="text-center text-sm text-slate-500 mb-6 leading-relaxed">
            {t('register.email.desc1')}<br />{t('register.email.desc2')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('register.email.label')}
              </label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError('') }}
                placeholder="example@email.com"
                autoFocus
                className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm placeholder-slate-400
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              {error && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400
                text-white text-sm font-semibold rounded-lg transition-colors
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {submitting ? t('common.saving') : t('register.email.submit')}
            </button>
          </form>

          <p className="mt-4 text-center text-[11px] text-slate-400">
            {t('register.email.note')}
          </p>
        </div>
      </div>
    </div>
  )
}
